import React, {useEffect, useState, useRef} from 'react';
import {HighStageVideoConfig} from '../../types';
import Video from '../videoPlaylist/videoModule';
import TextBox from './textBox';
import TextBoxMuted from './textBoxMuted';
import useCurrentBreakpoint from '../../hooks/useCurrentBreakpoint';

type HighStageConfig = {
  'jsonConfig': string
}

const HighStageVideo = (props: HighStageConfig): JSX.Element => {
  const {jsonConfig} = props;
  const {currentBreakpoint} = useCurrentBreakpoint();
  const config: HighStageVideoConfig = jsonConfig ? JSON.parse(jsonConfig) : null;
  const waitForCurrenTimeOfVideoTimeoutTime = 400;
  const lazyLoadingTimeoutTime = 1000;
  const container = useRef<HTMLDivElement>(null);
  const videoContainer = useRef<HTMLDivElement>(null);
  const containerId = `highStageVideo${config.videoId}`;
  const [playTrigger, setPlayTrigger] = useState(0);
  const [videoIsStarted, setVideoIsStarted] = useState(config.videoConfig.muted ? true : false);
  const [videoPaused, setVideoPaused] = useState(config.videoConfig.muted ? false : true);
  let videoIsLoaded: boolean = false;
  let waitForCurrenTimeOfVideo: number;

  const getVideo = (): HTMLVideoElement | null => videoContainer?.current?.querySelector(`video`) || null;
  const isVideoStarted = (video: HTMLVideoElement) => (video.currentTime > 0 && !video.ended && video.readyState > 2);
  const initialStartForMutedVideo = (): void => {
    if (!videoIsLoaded && config.videoConfig.muted ) {
      toggleVideo();
      videoIsLoaded = true;
    }
  }
  const checkIfVideoIsStarted = () => {
      let isVideoInDomTimeout;
      const video: HTMLVideoElement | null = getVideo();
      if (!video) {
        // needed for lazyloaded Videos
        isVideoInDomTimeout =  window.setTimeout(checkIfVideoIsStarted, lazyLoadingTimeoutTime);
      } else {
        // lazyloading Timeout can be cleared
        window.clearTimeout(isVideoInDomTimeout);
        initialStartForMutedVideo();
        // timeout is needed because the video.currentTime has some delay
        waitForCurrenTimeOfVideo = window.setTimeout(() => {
          setVideoIsStarted(isVideoStarted(video));
          setVideoPaused(video.paused);
        }, waitForCurrenTimeOfVideoTimeoutTime);
      }
  }

  const toggleVideo = (): void => {
    const video: HTMLVideoElement | null = getVideo();
    if (video) {
      video.paused ? setPlayTrigger(playTrigger + 1) : video.pause();
      setVideoPaused(video.paused);
    }
  }

  const videoEnded = (): void => {
    if (config.videoConfig.muted) {
      setPlayTrigger(playTrigger + 1);
      setVideoPaused(false);
    } else {
      setVideoIsStarted(false);
      setVideoPaused(true);
    }
  }

  const shouldShowTextBox = (): boolean => {
    // show textbox only if video is not muted and in breakpoints from md to xl only if its not started
    return !config.videoConfig.muted && (!videoIsStarted || !['xl', 'lg', 'md'].includes(currentBreakpoint));
  }

  useEffect(() => {
    const containerDiv = container.current;
    const playerCoverForLazyLaoding = videoContainer.current;
    containerDiv?.addEventListener("keydown", checkIfVideoIsStarted);
    containerDiv?.addEventListener("mousedown", checkIfVideoIsStarted);
    checkIfVideoIsStarted();
    return () => {
      containerDiv?.removeEventListener("mousedown", checkIfVideoIsStarted);
      containerDiv?.removeEventListener("keydown", checkIfVideoIsStarted);
      window.clearTimeout(waitForCurrenTimeOfVideo);
    }
  }, []);

  return (
    <div id={containerId} ref={container} className="stage-gallery l-fullwidth high-stage-gallery video">
      <div className="stage-gallery__slider">
        <div aria-label={config.videoConfig.muted ? config.textbox.description : undefined}
             className={`stage-gallery__image-container text-box--${config.textbox.background}`}>
          <Video ref={videoContainer}
                 videoId={config.videoId}
                 movingImage={config.videoConfig}
                 onVideoEnded={videoEnded}
                 playTrigger={playTrigger}
                 muted={config.videoConfig.muted}
          />
          {config.videoConfig.muted &&
            <TextBoxMuted textbox={config.textbox} dictionary={config.dictionary} link={config.link}
                          paused={videoPaused} toggleVideoPlay={toggleVideo}/>
          }
          {shouldShowTextBox() &&
            <TextBox textbox={config.textbox}/>
          }
        </div>
      </div>
    </div>
  );
}

export default HighStageVideo;
